import { useEffect, useState } from 'react';
import { useLivenessTicker } from './useLivenessTicker';
import { useWallClock } from './useWallClock';
import { apiFetch, ApiError } from '../../services/api-client';
import { getRedemptionSecret } from '../../services/shop-service';
import { CollectibleSprite } from './collectible-icons';

interface Props {
  nftId: string;
  shopId: string;
  shopName: string;
  logoUrl?: string | null;
  itemTitle?: string | null;
  /** Fires once the server row exists, so the caller can burn on-chain. */
  onBurned: (requestId: string) => void;
}

const ERRORS: Record<string, string> = {
  reward_already_redeemed: 'ALREADY REDEEMED',
  already_redeemed: 'ALREADY REDEEMED',
  reward_not_found: 'NO REWARD HERE — IT MAY ALREADY BE USED',
  // Deliberately the same message as reward_not_found: separate codes for the
  // logs, one message at the counter, since the distinction only helps someone
  // probing for a forgery that works.
  not_a_reward: 'NO REWARD HERE — IT MAY ALREADY BE USED',
  not_your_reward: "THIS REWARD ISN'T ON THIS ACCOUNT",
  wrong_shop: 'EARNED AT ANOTHER SHOP',
  chain_unavailable: "CAN'T VERIFY RIGHT NOW — TRY AGAIN",
};

function SecretWord({ word }: { word: string }) {
  return (
    <div className="my-3">
      <p className="font-heading text-[8px] text-body-grey tracking-[1px]">TODAY'S WORD</p>
      <p className="font-grotesk font-bold text-lg text-coral">✦ {word} ✦</p>
    </div>
  );
}

/**
 * The reward itself is the hero. Nik's counter feedback 2026-08-10: at
 * w-16 the image was too small to read across a counter, and the shop logo
 * was standing in for an item the customer never actually saw.
 *
 * The shop logo survives as a small corner chip — it answers "where did this
 * come from", which is provenance, not identity. It renders nothing at all
 * when absent or broken, never a broken-image frame.
 */
function RewardHero({ url, name }: { url?: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  const showChip = Boolean(url) && !failed;
  return (
    <div className="relative w-32 h-32 mx-auto mb-3">
      <span data-testid="reward-hero-sprite" className="block w-full h-full text-coral">
        <CollectibleSprite icon="keepcup" className="w-full h-full" />
      </span>
      {showChip && (
        <img
          src={url as string}
          alt={name}
          onError={() => setFailed(true)}
          className="absolute -bottom-1 -right-1 w-9 h-9 object-contain bg-white pixel-corners-sm border-[3px] border-primary [image-rendering:pixelated]"
        />
      )}
    </div>
  );
}

/**
 * The counter surface.
 *
 * The button addresses the BARISTA deliberately: their tap is what burns the
 * reward, and a screenshot cannot respond to a tap — so the barista is not
 * judging evidence, they caused the event. That is the property every other
 * option lacked. Showing a "redeemed" screen, or even tapping an NFC puck,
 * still ends with the barista squinting at something a screenshot could
 * imitate; NFC proves the burn was real, it does not make the glance
 * trustworthy.
 *
 * There is deliberately NO customer-facing redeem button. If a customer could
 * spend a reward from the couch, the barista's tap would stop being the moment
 * of truth and the whole property collapses.
 *
 * The shop's secret word and the live wall clock are shown BEFORE the tap so
 * the barista has something to check against reality (the till, staff
 * training) before they act — a screenshot shows a stale clock and, if it's
 * missing the secret, no word to check against at all.
 */
export default function RewardCard({ nftId, shopId, shopName, logoUrl, itemTitle, onBurned }: Props) {
  const [redeemedAt, setRedeemedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const clock = useWallClock();

  useEffect(() => {
    let alive = true;
    getRedemptionSecret(shopId).then(s => { if (alive) setSecret(s); });
    return () => { alive = false; };
  }, [shopId]);

  async function handleBaristaTap() {
    if (busy || redeemedAt) return; // one burn per card, however fast the taps
    setBusy(true);
    setError(null);
    try {
      // apiFetch, not a bare fetch: /api/redeem is an authenticated write like
      // every other one, and a bare fetch sends no bearer — the endpoint 401s
      // before the row is inserted, so the tap silently never redeems.
      const body = await apiFetch<{ id: string; redeemed_at: string }>('/api/redeem', {
        action: 'redeem',
        nftId,
        shopId,
      });
      // State 2 fires the moment the SERVER row exists. onBurned is called but
      // never awaited: the on-chain burn takes seconds and runs in the
      // background — making a barista watch a chain confirmation mid-service
      // is the exact thing this design exists to avoid.
      setRedeemedAt(body.redeemed_at);
      // The coffee is authorised by the server row and is being handed over
      // NOW. Anything that fails downstream (the chain burn, the collectible
      // lookup) must not tell the barista the redemption failed — recovery
      // retries the burn later.
      try { onBurned(body.id); } catch { /* deliberately swallowed */ }
    } catch (e) {
      // apiFetch surfaces the server's `error` code as the message.
      const code = e instanceof ApiError ? e.message : '';
      // An unmapped code is a server message nobody would otherwise ever see.
      // The nft_level outage hid here for a full day.
      if (!ERRORS[code]) console.error('redeem failed:', code);
      setError(ERRORS[code] || 'Redeem failed — try again');
    } finally {
      setBusy(false);
    }
  }

  if (redeemedAt) {
    return <Redeemed shopName={shopName} redeemedAt={redeemedAt} logoUrl={logoUrl} secret={secret} />;
  }

  return (
    <div className="bg-white rounded-[16px] p-7 text-center">
      <RewardHero url={logoUrl} name={shopName} />
      <p className="font-grotesk font-medium text-[13px] text-body-grey mb-1">{shopName}</p>
      <p className="font-grotesk font-bold text-3xl text-primary mb-1">{itemTitle || 'FREE COFFEE'}</p>
      {secret && <SecretWord word={secret} />}
      {/* Live clock: proof the screen is not a screenshot, before the tap. */}
      <p className="font-grotesk font-medium text-[13px] text-body-grey mb-4 tabular-nums" aria-live="off">{clock}</p>
      <button
        onClick={handleBaristaTap}
        disabled={busy}
        className="rounded-full bg-coral text-white w-full font-grotesk font-bold text-base py-8 leading-tight disabled:opacity-40"
      >
        {busy ? 'REDEEMING…' : 'BARISTA: TAP TO GIVE COFFEE'}
      </button>
      {error && <p className="font-grotesk font-medium text-[12px] text-error mt-3">{error}</p>}
    </div>
  );
}

function Redeemed({ shopName, redeemedAt, logoUrl, secret }: {
  shopName: string; redeemedAt: string; logoUrl?: string | null; secret: string | null;
}) {
  const secs = useLivenessTicker(redeemedAt);
  return (
    <div className="bg-white rounded-[16px] p-7 text-center border-2 border-[#2f9440]">
      <RewardHero url={logoUrl} name={shopName} />
      <p className="font-grotesk font-bold text-xl text-[#2f9440] mb-1">✓ REDEEMED</p>
      <p className="font-grotesk font-bold text-[13px] text-primary mb-3">HAND OVER THE COFFEE</p>
      {secret && <SecretWord word={secret} />}
      {/* The ticking IS the anti-reuse control: a screenshot is frozen. */}
      <p className="font-grotesk font-medium text-[13px] text-body-grey" aria-live="polite">{secs}s ago</p>
    </div>
  );
}
